import React, { useState, useEffect, useRef } from 'react';
import { Cross } from '@saleshandy/icons';
import {
  FilterLogic,
  FilterOperation,
  FilterCondition,
  FilterRow,
  FilterBuilderProps,
  FilterRowProps,
  FilterConditionProps,
  OPERATIONS_MAPPING,
  RangeValue,
} from './standalone-filter-types';
import SelectFilter from '../../../../shared/design-system/components/molecules/prospects-filter/select-filter';

// Multi-select component for values
interface MultiSelectProps {
  values: (string | number)[];
  onChange: (values: (string | number)[]) => void;
  options?: { key: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  values,
  onChange,
  options = [],
  placeholder = 'Select values...',
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const multiSelectRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const removeValue = (valueToRemove: string | number) => {
    onChange(values.filter((value) => value !== valueToRemove));
  };

  const addValue = (valueToAdd: string | number) => {
    if (!values.includes(valueToAdd)) {
      onChange([...values, valueToAdd]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        multiSelectRef.current &&
        !multiSelectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      ref={multiSelectRef}
      className={`multi-select ${className} ${disabled ? 'disabled' : ''}`}
    >
      <div
        className="multi-select-trigger"
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="multi-select-values">
          {values.length === 0 ? (
            <span className="multi-select-placeholder">{placeholder}</span>
          ) : (
            values.map((value) => (
              <span key={value} className="multi-select-tag">
                {value}
                <button
                  type="button"
                  className="multi-select-tag-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeValue(value);
                  }}
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>
        <span className="multi-select-arrow">▼</span>
      </div>
      {isOpen && !disabled && (
        <div className="multi-select-dropdown">
          <input
            type="text"
            className="multi-select-search"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {filteredOptions.map((option) => (
            <div
              key={option.key}
              className={`multi-select-option ${
                values.includes(option.key) ? 'selected' : ''
              }`}
              onClick={() => addValue(option.key)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Filter Condition Component
const FilterConditionComponent: React.FC<FilterConditionProps> = ({
  condition,
  conditionIndex,
  fields,
  onConditionChange,
  onRemoveCondition,
  showLogic,
  logic,
  onLogicChange,
  valueOptions = [],
  allSelectedFields = [],
  disabled = false,
}) => {
  const handleFieldChange = (fieldKey: string) => {
    const field = fields.find((f) => f.key === fieldKey);
    const newCondition = {
      ...condition,
      field: field || null,
      operation: field?.operations?.[0] || null,
      value: undefined,
    };
    onConditionChange(newCondition);
  };

  const handleOperationChange = (operation: FilterOperation) => {
    const newCondition = {
      ...condition,
      operation,
      value: undefined,
    };
    onConditionChange(newCondition);
  };

  const handleValueChange = (value: string | number | (string | number)[]) => {
    const newCondition = {
      ...condition,
      value,
    };
    onConditionChange(newCondition);
  };

  // Fixed percentage options for current ESP design
  const percentageOptions = [10, 20, 30, 40, 50, 60].map((value) => ({
    key: value,
    label: `${value}%`,
  }));

  const currentRangeValue: RangeValue = (condition.value as RangeValue) || {};

  const renderValueInputContent = () => {
    if (condition.operation === FilterOperation.IS) {
      return (
        <div className="value-range-selects minimum-max-selects d-flex align-items-center">
          <SelectFilter
            placeholder="Min"
            options={percentageOptions}
            labelKey="label"
            uniqueKey="key"
            isMultiSelect={false}
            values={
              currentRangeValue.min !== undefined &&
              currentRangeValue.min !== null
                ? [
                    percentageOptions.find(
                      (opt) =>
                        Number(opt.key) === Number(currentRangeValue.min),
                    ),
                  ]
                : []
            }
            onSelect={(selectedOptions) => {
              const selected = selectedOptions && selectedOptions[0];
              const minVal =
                selected != null && selected.key != null
                  ? Number(selected.key)
                  : undefined;
              const updatedRange: RangeValue = {
                ...currentRangeValue,
                min:
                  typeof minVal === 'number' && !Number.isNaN(minVal)
                    ? minVal
                    : undefined,
              };
              handleValueChange(updatedRange as any);
            }}
            className="value-select min-select"
          />
          <span
            className="value-range-to-label mx-2"
            style={{
              color: '#6B7280',
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '20px',
            }}
          >
            to
          </span>
          <SelectFilter
            placeholder="Max"
            options={percentageOptions}
            labelKey="label"
            uniqueKey="key"
            isMultiSelect={false}
            values={
              currentRangeValue.max !== undefined &&
              currentRangeValue.max !== null
                ? [
                    percentageOptions.find(
                      (opt) =>
                        Number(opt.key) === Number(currentRangeValue.max),
                    ),
                  ]
                : []
            }
            onSelect={(selectedOptions) => {
              const selected = selectedOptions && selectedOptions[0];
              const maxVal =
                selected != null && selected.key != null
                  ? Number(selected.key)
                  : undefined;
              const updatedRange: RangeValue = {
                ...currentRangeValue,
                max:
                  typeof maxVal === 'number' && !Number.isNaN(maxVal)
                    ? maxVal
                    : undefined,
              };
              handleValueChange(updatedRange as any);
            }}
            className="value-select max-select"
          />
        </div>
      );
    }

    if (
      [
        FilterOperation.IN,
        FilterOperation.NOT_IN,
        FilterOperation.IS_ANY,
        FilterOperation.IS_NOT_ANY,
      ].includes(condition.operation as FilterOperation)
    ) {
      return (
        <MultiSelect
          values={Array.isArray(condition.value) ? condition.value : []}
          onChange={handleValueChange}
          options={valueOptions || []}
          placeholder="Select values"
          className="value-multi-select"
        />
      );
    }

    return (
      <SelectFilter
        placeholder="Select value"
        isMultiSelect={true}
        options={valueOptions}
        labelKey="label"
        uniqueKey="key"
        values={(() => {
          if (!condition.value) {
            return [];
          }

          // Convert condition.value to array if it's not already
          const valueArray = Array.isArray(condition.value)
            ? condition.value
            : [condition.value];

          // Map the keys to full option objects
          return valueArray
            .map((key) => valueOptions?.find((opt) => opt.key === key))
            .filter((opt) => opt !== undefined);
        })()}
        onSelect={(selectedOptions) => {
          // Extract all selected keys as an array
          const selectedKeys = selectedOptions
            ? selectedOptions.map((opt) => opt.key)
            : [];
          handleValueChange(selectedKeys);
        }}
        className="value-select"
        showCount={true}
      />
    );
  };

  // Show all fields but disable already selected ones (except current condition's field)
  const currentFieldKey = condition?.field?.key;
  const fieldOptions = fields.map((field) => ({
    key: field.key,
    label: field.label,
    isDisabled:
      allSelectedFields.includes(field.key) && field.key !== currentFieldKey,
  }));

  const operationOptions =
    condition.field?.operations.map((op) => ({
      key: op,
      label: OPERATIONS_MAPPING[op] || op,
    })) || [];

  // Auto-select operation if there's only one option and it's not already selected
  useEffect(() => {
    if (
      condition.field &&
      operationOptions.length === 1 &&
      !condition.operation
    ) {
      handleOperationChange(operationOptions[0].key as FilterOperation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition.field?.key, operationOptions.length]);

  const logicOptions = [
    { key: FilterLogic.AND, label: 'And' },
    // { key: FilterLogic.OR, label: 'Or' },
  ];

  return (
    <div className="filter-condition-row">
      <div className="condition-row-section">
        {showLogic && conditionIndex > 0 && (
          <div className="condition-logic">
            {/* warmup filter used this logic text instead of select filter */}
            <span className="logic-text">
              {logic
                ? logicOptions.find((opt) => opt.key === logic)?.label || 'OR'
                : 'OR'}
            </span>
            {/* {conditionIndex === 1 ? (
              <SelectFilter
                placeholder="Select Logic"
                options={logicOptions}
                labelKey="label"
                uniqueKey="key"
                values={
                  logic ? [logicOptions.find((opt) => opt.key === logic)] : []
                }
                onSelect={(selectedOptions) => {
                  if (selectedOptions && selectedOptions[0]) {
                    onLogicChange(selectedOptions[0].key as FilterLogic);
                  }
                }}
                isMultiSelect={false}
                className="logic-select"
              />
            ) : (
              <span className="logic-text">
                {logic
                  ? logicOptions.find((opt) => opt.key === logic)?.label || 'OR'
                  : 'OR'}
              </span>
            )} */}
          </div>
        )}

        {conditionIndex === 0 && (
          <div className="condition-logic" style={{ marginTop: '8px' }}>
            <span className="where-label">Where</span>
          </div>
        )}

        <SelectFilter
          placeholder="Select ESP"
          options={fieldOptions}
          labelKey="label"
          uniqueKey="key"
          values={
            condition.field
              ? [fieldOptions.find((opt) => opt.key === condition.field?.key)]
              : []
          }
          onSelect={(selectedOptions) => {
            if (selectedOptions && selectedOptions[0]) {
              handleFieldChange(selectedOptions[0].key);
            }
          }}
          isMultiSelect={false}
          className="field-select"
        />

        {condition.field && (
          <>
            {operationOptions.length === 1 ? (
              // Show simple tag/button when only one operation is available
              <div className="operation-tag">{operationOptions[0].label}</div>
            ) : (
              <SelectFilter
                placeholder="Select Operation"
                options={operationOptions}
                labelKey="label"
                uniqueKey="key"
                values={
                  condition.operation
                    ? [
                        operationOptions.find(
                          (opt) => opt.key === condition.operation,
                        ),
                      ]
                    : []
                }
                onSelect={(selectedOptions) => {
                  if (selectedOptions && selectedOptions[0]) {
                    handleOperationChange(
                      selectedOptions[0].key as FilterOperation,
                    );
                  }
                }}
                isMultiSelect={false}
                className="operation-select"
              />
            )}
          </>
        )}

        {condition.field && condition.operation && (
          <div className="value-input">{renderValueInputContent()}</div>
        )}
      </div>

      <button
        type="button"
        className="remove-condition-btn"
        onClick={onRemoveCondition}
        title="Remove condition"
      >
        <Cross width={16} height={16} color="#6B7280" />
      </button>
    </div>
  );
};

// Filter Row Component
const FilterRowComponent: React.FC<FilterRowProps> = ({
  row,
  fields,
  onRowChange,
  onAddCondition,
  onRemoveCondition,
  canAddCondition,
  maxConditionsReached,
  valueOptions = [],
  allSelectedFields = [],
  disabled = false,
}) => {
  const handleConditionChange = (
    conditionIndex: number,
    updatedCondition: FilterCondition,
  ) => {
    const newConditions = [...row.conditions];
    newConditions[conditionIndex] = updatedCondition;
    onRowChange({ ...row, conditions: newConditions });
  };

  const handleLogicChange = (newLogic: FilterLogic) => {
    onRowChange({ ...row, logic: newLogic });
  };

  return (
    <div className="filter-row">
      {row.conditions.map((condition, conditionIndex) => (
        <FilterConditionComponent
          key={condition.id}
          condition={condition}
          conditionIndex={conditionIndex}
          fields={fields}
          onConditionChange={(updatedCondition) =>
            handleConditionChange(conditionIndex, updatedCondition)
          }
          onRemoveCondition={() => onRemoveCondition(conditionIndex)}
          showLogic={conditionIndex > 0}
          logic={row.logic}
          onLogicChange={handleLogicChange}
          valueOptions={valueOptions}
          allSelectedFields={allSelectedFields}
          disabled={disabled}
        />
      ))}

      <div className="add-filter-section">
        <button
          type="button"
          className={`add-filter-btn ${
            !canAddCondition || maxConditionsReached ? 'disabled' : ''
          }`}
          onClick={onAddCondition}
          disabled={!canAddCondition || maxConditionsReached || disabled}
        >
          + Add Filter
        </button>
      </div>
    </div>
  );
};

// Helper: condition has complete value (for range: both min and max must be set as numbers)
const isConditionValueComplete = (condition: FilterCondition): boolean => {
  const value = condition?.value;
  if (value === null || value === undefined) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  // Range value: if object has min or max, BOTH are mandatory
  if (
    typeof value === 'object' &&
    value !== null &&
    ('min' in value || 'max' in value)
  ) {
    const range = value as RangeValue;
    const { min, max } = range;
    const numMin = Number(min);
    const numMax = Number(max);
    return (
      min !== undefined &&
      min !== null &&
      max !== undefined &&
      max !== null &&
      !Number.isNaN(numMin) &&
      !Number.isNaN(numMax)
    );
  }
  return true;
};

// Main Filter Builder Component
const FilterBuilder: React.FC<FilterBuilderProps> = ({
  fields,
  filterRows,
  onFilterRowsChange,
  maxConditions = 30,
  className = '',
  valueOptions = [],
  disabled = false,
  hasError = false,
}) => {
  // Ensure filterRows is always an array (can be object e.g. { logic, conditions } from URL/API)
  const safeFilterRows = Array.isArray(filterRows) ? filterRows : [];

  const updateFilterRow = (rowIndex: number, updatedRow: FilterRow) => {
    const newRows = [...safeFilterRows];
    newRows[rowIndex] = updatedRow;
    onFilterRowsChange(newRows);
  };

  const addConditionToRow = (rowIndex: number) => {
    const row = safeFilterRows[rowIndex];
    const newCondition: FilterCondition = {
      id: `condition-${Date.now()}`,
      field: null,
      operation: null,
      value: undefined,
    };
    const updatedRow = {
      ...row,
      conditions: [...row.conditions, newCondition],
    };
    updateFilterRow(rowIndex, updatedRow);
  };

  const removeConditionFromRow = (rowIndex: number, conditionIndex: number) => {
    const row = safeFilterRows[rowIndex];
    if (row.conditions.length === 1) {
      // If it's the last condition, reset it instead of removing
      const resetCondition: FilterCondition = {
        id: `condition-${Date.now()}`,
        field: null,
        operation: null,
        value: undefined,
      };
      const updatedRow = {
        ...row,
        conditions: [resetCondition],
      };
      updateFilterRow(rowIndex, updatedRow);
    } else {
      const updatedRow = {
        ...row,
        conditions: row.conditions.filter(
          (_, index) => index !== conditionIndex,
        ),
      };
      updateFilterRow(rowIndex, updatedRow);
    }
  };

  const totalConditions = safeFilterRows.reduce(
    (count, row) => count + row.conditions.length,
    0,
  );
  const maxConditionsReached = totalConditions >= maxConditions;

  // Calculate all selected field keys across all rows and conditions
  const getAllSelectedFields = (): string[] => {
    const selectedFields: string[] = [];
    safeFilterRows.forEach((row) => {
      row.conditions.forEach((condition) => {
        if (condition.field?.key) {
          selectedFields.push(condition.field.key);
        }
      });
    });
    return selectedFields;
  };

  const allSelectedFields = getAllSelectedFields();

  return (
    <div
      className={`filter-builder ${className} ${
        disabled ? 'filter-builder-disabled' : ''
      } ${hasError ? 'filter-builder-error' : ''}`}
    >
      {safeFilterRows.map((row, rowIndex) => (
        <FilterRowComponent
          key={row.id}
          row={row}
          fields={fields}
          onRowChange={(updatedRow) => updateFilterRow(rowIndex, updatedRow)}
          onAddCondition={() => addConditionToRow(rowIndex)}
          onRemoveCondition={(conditionIndex) =>
            removeConditionFromRow(rowIndex, conditionIndex)
          }
          disabled={disabled}
          canAddCondition={
            row.conditions.length < 10 &&
            row.conditions.every(
              (condition) =>
                condition.field !== null &&
                condition.operation !== null &&
                isConditionValueComplete(condition),
            )
          } // Max conditions per row; all conditions must have field, operation, and complete value (both min & max for range)
          maxConditionsReached={maxConditionsReached}
          valueOptions={valueOptions}
          allSelectedFields={allSelectedFields}
        />
      ))}
    </div>
  );
};

export default FilterBuilder;
export { FilterRowComponent, FilterConditionComponent, MultiSelect };
